import axios from "../utils/axios";

/**
 * Send OTP to the specified email address
 * @param {string} email - Email address to send OTP to
 * @returns {Promise<Object>} Response with success status and message
 */
export const sendOtp = async (email) => {
  try {
    const response = await axios.post("/api/auth/send-otp", { email });

    return {
      success: true,
      message: response.data.message || "OTP sent successfully",
      data: response.data.data,
    };
  } catch (error) {
    console.error("Send OTP error:", error);

    let errorMessage = "Failed to send OTP. Please try again.";

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (!error.response || error.code === 'ERR_NETWORK') {
      errorMessage = "Service temporarily unavailable. Please try again later.";
    } else if (error.response?.status === 404) {
      errorMessage = "Service temporarily unavailable. Please try again later.";
    } else if (error.response?.status === 429) {
      errorMessage = "Too many requests. Please try again later.";
    } else if (error.response?.status >= 500) {
      errorMessage = "Server error. Please try again later.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
    };
  }
};

/**
 * Verify OTP and authenticate user
 * @param {string} email - Email address
 * @param {string} otp - One-time password
 * @returns {Promise<Object>} Response with token and user data
 */
export const verifyOtp = async (email, otp) => {
  try {
    const response = await axios.post("/api/auth/verify-otp", { email, otp });

    return {
      success: true,
      token: response.data.data?.token || response.data.token,
      user: response.data.data?.user || response.data.user,
      message: response.data.message || "Login successful",
    };
  } catch (error) {
    console.error("Verify OTP error:", error);

    let errorMessage = "Invalid OTP. Please try again.";

    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 401) {
      errorMessage = "Invalid or expired OTP. Please request a new one.";
    } else if (error.response?.status === 404) {
      errorMessage = "OTP session not found. Please request a new OTP.";
    } else if (error.response?.status === 429) {
      errorMessage = "Too many attempts. Please try again later.";
    } else if (error.response?.status >= 500) {
      errorMessage = "Server error. Please try again later.";
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

/**
 * Login with email and password (existing function for reference)
 * @param {string} email - User email
 * @param {string} password - User password
 * @returns {Promise<Object>} Response with token and user data
 */
export const login = async (email, password) => {
  try {
    const response = await axios.post("/api/auth/login", { email, password });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get current user profile
 * @returns {Promise<Object>} User profile data
 */
export const getUserProfile = async () => {
  try {
    const response = await axios.get("/api/auth/me");
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Logout user
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    await axios.post("/api/auth/logout");
  } catch (error) {
    console.error("Logout API error:", error);
  } finally {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }
};
